// The `dictation/logs` api module — THE t11nClient SPLIT (D1.1, ARCFE-1084).
//
// WHAT MOVED, AND IT IS EXACTLY THE TAIL BLOCK THE CENSUS NAMED. The five
// log-CRUD fns (`getDictations`, `searchDictations`, `getDictationStats`,
// `deleteDictation`, `deleteDictations`, formerly L463-519 of
// `src/lib/SessionRecordingProvider/t11nClient.ts`) and exactly the schemas and
// types they parse with. Each body is byte-identical to its source apart from
// the transport identifier: `t11nClient` -> `t11nHttp`, the loader façade over
// the SAME app singleton. Routes, param objects, the `?? undefined` collapses,
// `encodeURIComponent`, the `:delete` POST body shape and every `.parse()` call
// are unchanged, and `logs.test.ts` pins each of them from the outside.
//
// WHAT DID NOT MOVE, and this is the half that makes the split reviewable by the
// module's owners. Nothing else in that file was touched: both axios singletons
// (`t11nClient`, `t11nClientV2`), both request interceptors (auth, `X-Country`,
// workspace-id, client identity, tracing, per-request base-URL resolution), the
// desktop confirmed-offline short-circuit, the region-resolving base-URL
// builders (`getTranscriptionBaseUrl`, `getTranscriptionBaseUrlV2`,
// `normalizeAzureApiProdHost`, the region->subdomain table), and the whole
// live-transcription surface (`patchSpeakerLabels`, `registerV2Recording`,
// `uploadV2Chunk`, `isRetryableRegisterError`) with every transcript schema it
// parses with. The moved names are re-exported from that module name-intact, so
// every importer and every `jest.mock` literal keeps resolving unchanged.
//
// WHY THE SCHEMAS ARE HERE AND NOT IN `/contracts`. zod schemas are RUNTIME
// VALUES; `/contracts` is the inert zone and its V3 fixture permits relative
// imports only. `DictationPercentileBand` and `DictationStats` come with them
// rather than being hand-copied into `/contracts`, which is the ruling the
// contracts slice recorded at ARCFE-1043 and is repeated at
// `contracts/logs.types.ts`: a hand-written band union would be a SECOND
// declaration of a shape zod already owns, drifting silently the first time the
// server adds a band. The inferred TS types re-export through the `/data` door.
import { z } from 'zod';

import { t11nHttp } from './t11nHttp';

export const DictationResponseSchema = z.object({
  dictation_id: z.string(),
  start_date: z.string().datetime(),
  duration_ms: z.number(),
  post_processed_text: z.string().nullable().optional(),
  // A future rating must not make the history list or search fail to parse.
  feedback_rating: z.enum(['thumb_up', 'thumb_down']).nullish().catch(undefined),
  match_count: z.number().nullable().optional(),
  highlights: z.array(z.string()).nullable().optional(),
});

export const DictationsResponseSchema = z.object({
  dictations: z.array(DictationResponseSchema),
  next_cursor: z.string().nullable().optional(),
  total: z.number().int().nonnegative().optional(),
});

// snake_case wire → camelCase domain shape.
export const DictationStatsResponseSchema = z
  .object({
    dictated_words: z.number().int().nonnegative(),
    // May be fractional (server-side aggregates).
    time_saved_ms: z.number().nonnegative(),
    speed_wpm: z.number().nonnegative(),
    // `.catch(undefined)` hides unknown future bands instead of failing parse.
    percentile_band: z
      .enum(['top_1', 'top_5', 'top_10', 'top_25', 'top_50'])
      .nullish()
      .catch(undefined),
  })
  .transform((s) => ({
    dictatedWords: s.dictated_words,
    timeSavedMs: s.time_saved_ms,
    speedWpm: s.speed_wpm,
    percentileBand: s.percentile_band ?? undefined,
  }));

export type DictationPercentileBand = NonNullable<
  z.infer<typeof DictationStatsResponseSchema>['percentileBand']
>;

export type DictationsResponse = z.infer<typeof DictationsResponseSchema>;
export type DictationStatsResponse = z.infer<typeof DictationStatsResponseSchema>;

/**
 * The stats tile's domain shape, carved from `src/features/dictationLogs/types.ts`.
 *
 * It rides with the schemas rather than with `DictationLogEntry` in
 * `/contracts` for one reason: its `percentileBand` field is typed by
 * `DictationPercentileBand`, which is INFERRED from the zod schema above. That
 * is precisely the dependency the C slice recorded as "held by severance cohort
 * S1" and deferred to D1. It is kept as a distinct declaration rather than
 * collapsed into `DictationStatsResponse` because the two are not the same type
 * — `percentileBand` is optional here and required-but-possibly-undefined
 * there — and unifying them would be a behaviour change smuggled into a move.
 */
export type DictationStats = {
  dictatedWords: number;
  timeSavedMs: number;
  speedWpm: number;
  percentileBand?: DictationPercentileBand;
};

export interface GetDictationsParams {
  cursor?: string | null;
  limit?: number;
  from?: string | null;
  to?: string | null;
}

export async function getDictations(
  params: GetDictationsParams = {}
): Promise<DictationsResponse> {
  const { data } = await t11nHttp.get<unknown>(`/dictations`, {
    params: {
      cursor: params.cursor ?? undefined,
      limit: params.limit,
      from: params.from ?? undefined,
      to: params.to ?? undefined,
    },
  });
  return DictationsResponseSchema.parse(data);
}

export interface SearchDictationsParams extends GetDictationsParams {
  q: string;
}

export async function searchDictations(
  params: SearchDictationsParams
): Promise<DictationsResponse> {
  const { data } = await t11nHttp.get<unknown>(`/dictations/search`, {
    params: {
      q: params.q,
      cursor: params.cursor ?? undefined,
      limit: params.limit,
      from: params.from ?? undefined,
      to: params.to ?? undefined,
    },
  });
  return DictationsResponseSchema.parse(data);
}

// Cap wait so a hung endpoint shows the "–" fallback, not an endless skeleton.
const DICTATION_STATS_TIMEOUT_MS = 10_000;

export async function getDictationStats(): Promise<DictationStatsResponse> {
  const { data } = await t11nHttp.get<unknown>(`/dictations/stats`, {
    timeout: DICTATION_STATS_TIMEOUT_MS,
  });
  return DictationStatsResponseSchema.parse(data);
}

export async function deleteDictation(dictationId: string): Promise<void> {
  await t11nHttp.delete(`/dictations/${encodeURIComponent(dictationId)}`);
}

export async function deleteDictations(dictationIds: string[]): Promise<void> {
  await t11nHttp.post('/dictations:delete', { dictation_ids: dictationIds });
}
